import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
	TextField,
	InputAdornment,
	IconButton,
	CircularProgress,
	Box,
	Chip,
	Stack,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { InstantSearch, Configure, useHits, useSearchBox } from 'react-instantsearch';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { clientSearch } from '@/utils/typesense/clients/clientSearch';
import { relationshipContactSearch } from '@/utils/typesense/clients/relationshipContactSearch';
import CheckIcon from '@mui/icons-material/Check';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { isValidEmail, MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from '@/utils/constants';
import { RELATIONSHIP_CONTACT_TYPE_LABELS } from '@/utils/relationshipContactOptions';
import { getObjectChipProps } from '@/utils/objectChip';

const KEYS = [',', ';', ' '];

const AutocompleteContactsRelationshipsInput = ({
	brokerGroupIds,
	setClient,
	disabled,
	attendees,
	error,
	onBlur = () => {},
}) => {
	const [query, setQuery] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const [selectedContacts, setSelectedContacts] = useState([]);

	const search = useCallback(
		async (searchQuery) => {
			if (
				!searchQuery?.trim() ||
				searchQuery.length < MIN_QUERY_LENGTH ||
				brokerGroupIds.length === 0
			) {
				setOptions([]);
				return;
			}

			setLoading(true);

			try {
				const brokerFilter = `brokerGroupId: [${brokerGroupIds.join(',')}]`;

				const [clientRes, relationshipRes] = await Promise.all([
					clientSearch.search([
						{
							indexName: 'clients/sort/name:asc',
							params: {
								query: searchQuery,
								hitsPerPage: 15,
								filters: brokerFilter,
							},
						},
					]),
					relationshipContactSearch.search([
						{
							indexName: 'relationshipContacts/sort/fullName:asc',
							params: {
								query: searchQuery,
								hitsPerPage: 15,
								filters: `${brokerFilter} && relationshipStatus:=active`,
							},
						},
					]),
				]);

				const clientHits = (clientRes?.results?.[0]?.hits ?? [])
					.filter((hit) => hit.contactEmail)
					.map((hit) => ({
						id: `client-${hit.id}-${hit.contactEmail}`,
						contactName: hit.contactName?.trim() || hit.name || hit.contactEmail,
						contactEmail: hit.contactEmail.trim(),
					}));

				const relationshipHits = (relationshipRes?.results?.[0]?.hits ?? [])
					.filter((hit) => hit.email)
					.map((hit) => ({
						id: `relationship-${hit.id}`,
						contactName: hit.fullName || hit.email,
						contactEmail: hit.email.trim(),
						relationshipType: hit.relationshipType,
					}));

				setOptions(
					[...clientHits, ...relationshipHits].sort((a, b) =>
						(a.contactName ?? a.contactEmail ?? '').localeCompare(
							b.contactName ?? b.contactEmail ?? '',
						),
					),
				);
			} catch (searchError) {
				console.error('[AutocompleteContactsInput] search failed', searchError);
				setOptions([]);
			} finally {
				setLoading(false);
			}
		},
		[brokerGroupIds],
	);

	useEffect(() => {
		const timeout = setTimeout(() => {
			search(query);
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timeout);
	}, [query, search]);

	useEffect(() => {
		if (!Array.isArray(attendees)) return;

		if (attendees.length > 0) {
			const initialContacts = attendees
				.map((attendee) => {
					const contactEmail = attendee.contactEmail ?? attendee.emailAddress;
					if (!contactEmail) return null;

					return {
						id: attendee.id ?? contactEmail,
						contactName: attendee.contactName ?? attendee.name ?? contactEmail,
						contactEmail,
						...(attendee.relationshipType && {
							relationshipType: attendee.relationshipType,
						}),
					};
				})
				.filter(Boolean);
			setSelectedContacts(initialContacts);
			return;
		}

		setSelectedContacts([]);
	}, [attendees]);

	const handleSearch = () => {
		if (query.length > 0) {
			search(query);
			if (isValidEmail(query)) {
				const newContact = {
					id: query,
					contactName: query,
					contactEmail: query,
				};
				const existingContactEmail = selectedContacts.map(
					(contact) => contact.contactEmail,
				);
				if (!existingContactEmail.includes(newContact.contactEmail)) {
					const updatedContacts = [...selectedContacts, newContact];
					setSelectedContacts(updatedContacts);
					setClient(updatedContacts);
				}
			}
		}
	};

	const handleBlur = (e) => {
		if (query.length > 0 && isValidEmail(query)) {
			handleSearch();
		}
		onBlur(e);
	};

	const handleSelectedContactDelete = (index) => {
		const updatedContacts = [...selectedContacts];
		updatedContacts.splice(index, 1);
		setSelectedContacts(updatedContacts);
		setClient(updatedContacts);
	};

	const handleReset = () => {
		setQuery('');
		setOptions([]);
		setOpen(false);
	};

	return (
		<>
			{selectedContacts.length > 0 && (
				<Box>
					{selectedContacts.map((contact, index) => (
						<Tooltip
							title={contact.contactEmail}
							placement='top'
							key={`chip-${contact.id ?? contact.contactEmail}`}
						>
							<Chip
								label={
									contact.relationshipType
										? `${contact.contactName} (${`${contact.relationshipType}`})`
										: contact.contactName
								}
								onDelete={() => handleSelectedContactDelete(index)}
								variant='contained'
								style={{ margin: '4px' }}
							/>
						</Tooltip>
					))}
				</Box>
			)}
			<Autocomplete
				id='contact-recipient-select'
				multiple
				value={selectedContacts}
				onBlur={handleBlur}
				loading={loading}
				loadingText='Loading contacts...'
				options={query.length >= MIN_QUERY_LENGTH ? options : []}
				open={open}
				disabled={Boolean(disabled)}
				noOptionsText={
					query.length >= MIN_QUERY_LENGTH ? 'No contacts found' : 'Type to search'
				}
				filterOptions={(x) => x}
				renderOption={(props, option) => (
					<Stack
						direction='row'
						alignItems='center'
						justifyContent='space-between'
						{...props}
						key={option.id}
						width='100%'
					>
						<Stack direction='row' alignItems='center' width='100%' spacing={1}>
							<Stack direction='row' alignItems='center' flexGrow={1}>
								<Typography>{option.contactName}</Typography>
								{option.relationshipType && (
									<Chip
										size='small'
										variant='outlined'
										label={
											RELATIONSHIP_CONTACT_TYPE_LABELS[
												option.relationshipType
											] ?? option.relationshipType
										}
										sx={(theme) => ({
											ml: '0.5em',
											borderColor: theme.palette.primary.main,
											color: theme.palette.primary.main,
										})}
									/>
								)}
							</Stack>
							<Typography variant='task_updated'>{option.contactEmail}</Typography>
						</Stack>
						{selectedContacts.some((c) => c.id === option.id) && (
							<CheckIcon sx={{ fontSize: '18px', ml: '0.5em' }} />
						)}
					</Stack>
				)}
				inputValue={query}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				getOptionLabel={(option) => option.contactName}
				onInputChange={(_, value, reason) => {
					if (reason !== 'reset') {
						setQuery(value);
					}
				}}
				onKeyDown={(e) => {
					if (e.key === 'Enter' || (KEYS.includes(e.key) && isValidEmail(query))) {
						e.preventDefault();
						if (isValidEmail(query)) {
							e.defaultMuiPrevented = true;
							handleSearch();
						}
					}
				}}
				renderInput={(params) => (
					<TextField
						error={error}
						placeholder='Search for existing contacts or enter emails'
						{...params}
						InputProps={{
							...params.InputProps,
							startAdornment: (
								<InputAdornment position='start'>
									<IconButton
										disabled={loading || disabled}
										sx={{ padding: '0' }}
										onClick={handleSearch}
									>
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position='end'>
									{loading ? (
										<CircularProgress color='inherit' size={20} />
									) : null}
									{query.length > 0 && (
										<IconButton
											size='small'
											disabled={loading || disabled}
											sx={{ padding: '0' }}
											onClick={handleReset}
										>
											<ClearIcon fontSize='20px' />
										</IconButton>
									)}
								</InputAdornment>
							),
						}}
					/>
				)}
				isOptionEqualToValue={(option, value) => option.id === value.id}
				onChange={(_, value) => {
					setSelectedContacts(value);
					setClient(value);
				}}
			/>
		</>
	);
};

const AutocompleteInput = ({
	query,
	refine,
	clear,
	hits,
	isSearching,
	setClient,
	clientId,
	disabled,
	clientName,
	error,
	width,
	onBlur = () => {},
	...props
}) => {
	const [open, setOpen] = useState(false);
	const [resetKey, setResetKey] = useState(0);
	const theme = useTheme();
	const chipProps = getObjectChipProps('prospect', { theme, isProspect: true });

	const { getFeatureFlags } = useFeatureFlagContext();
	const showProspects = Boolean(getFeatureFlags('prospects')?.active);

	const options = useMemo(() => (query.length > 0 ? hits : []), [query, hits]);

	useEffect(() => {
		refine(clientName);
	}, [clientName]);

	const handleSearch = () => {
		if (query.length > 0) {
			refine(query);
		}
	};

	const handleReset = () => {
		setClient(null);
		setOpen(false);
		clear();
		// setSelectedContacts([]);
	};

	useEffect(() => {
		setResetKey((prev) => prev + 1);
	}, [clientName]);

	return (
		<>
			<Autocomplete
				// id='client-select'
				{...{ width }}
				key={`search-client-${resetKey}`}
				loading={isSearching}
				loadingText={'Loading clients...'}
				options={options}
				open={open}
				disabled={Boolean(disabled)}
				noOptionsText={query.length > 0 ? 'No clients found' : 'Type to search'}
				// value={value}
				filterOptions={(x) => x}
				renderOption={(props, option) => (
					<Box
						display={'flex'}
						sx={{ alignItems: 'center', justifyContent: 'space-between' }}
						{...props}
						key={`client-select-${option.id}`}
					>
						<Box flexGrow={1} textAlign='left'>
							<Stack direction='row'>
								{`${option.name} ${
									option?.fileMigrationInProgress
										? '(File migration in progress)'
										: ''
								}`}
								{option.isProspect && showProspects && (
									<Chip
										size='small'
										label='PROSPECT'
										variant='outlined'
										color='primary'
										sx={{
											...chipProps,
											ml: '1em',
										}}
									/>
								)}
							</Stack>
						</Box>
						{option.objectID === clientId && <CheckIcon sx={{ fontSize: '18px' }} />}
					</Box>
				)}
				getOptionDisabled={(option) => option?.fileMigrationInProgress}
				inputValue={query}
				onOpen={() => setOpen(true)}
				onClose={() => setOpen(false)}
				onBlur={onBlur}
				getOptionLabel={(option) => option.name}
				onInputChange={(_, value, reason) => {
					if (reason !== 'reset') {
						refine(value);
					}
				}}
				renderInput={(params) => (
					<TextField
						error={Boolean(error)}
						placeholder={
							clientId
								? hits.find((hit) => hit.id === clientId)?.name ||
									((clientName ?? '').length > 0
										? clientName
										: 'Search for client')
								: 'Search for client'
						}
						{...params}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								handleSearch();
							} else if (e.key === 'Backspace' && clientId) {
								console.log('reset value', query, clientName, clientId);
								e.preventDefault();
								setResetKey((prev) => prev + 1);
								handleReset();
							}
						}}
						InputProps={{
							...params.InputProps,
							startAdornment: (
								<InputAdornment position='start'>
									<IconButton
										disabled={Boolean(isSearching || disabled)}
										sx={{ padding: '0' }}
										onClick={handleSearch}
									>
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position='end'>
									{isSearching ? (
										<CircularProgress color='inherit' size={20} />
									) : null}
									{query.length > 0 && (
										<IconButton
											size='small'
											disabled={Boolean(isSearching || disabled)}
											sx={{ padding: '0' }}
											onClick={handleReset}
										>
											<ClearIcon fontSize='20px' />
										</IconButton>
									)}
								</InputAdornment>
							),
						}}
					/>
				)}
				isOptionEqualToValue={(option, value) => {
					return option.id === value.id;
				}}
				onChange={(_, value) => {
					setClient(value);
					refine(value?.name ?? '');
				}}
			/>
		</>
	);
};

export const InstantClientSelect = ({
	clientId,
	clientName,
	setClient,
	disabled,
	showContact,
	filterProspects,
	attendees = [],
	error,
	onBlur = () => {},
	...props
}) => {
	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;

	const userQuery = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const brokerGroupIds = userQuery.data?.brokerGroupArray ?? [];

	if (showContact) {
		return (
			<AutocompleteContactsRelationshipsInput
				brokerGroupIds={brokerGroupIds}
				disabled={disabled || userQuery.isLoading}
				attendees={attendees}
				setClient={setClient}
				error={error}
				onBlur={onBlur}
			/>
		);
	}

	const clientNameFilter = clientName ? `name: "${clientName}"` : '';
	const filterSchema = useMemo(() => {
		const schema = {
			filters: `brokerGroupId: [${brokerGroupIds.join(',')}]${
				clientNameFilter ? ` && ${clientNameFilter}` : ''
			}${filterProspects ? ' && isProspect:!=true' : ''}`,
		};
		// console.log('Filter schema', schema);
		return schema;
	}, [brokerGroupIds, clientName]);

	return (
		<InstantSearch searchClient={clientSearch} indexName='clients'>
			<Configure hitsPerPage={15} {...filterSchema} />
			<InstantClientSelectWrapper
				disabled={Boolean(disabled || userQuery.isLoading)}
				{...{ setClient, clientId, clientName, error, onBlur }}
			/>
		</InstantSearch>
	);
};

const InstantClientSelectWrapper = ({
	setClient,
	clientId,
	disabled,
	clientName,
	error,
	width,
	onBlur,
	...props
}) => {
	const { items } = useHits(props);

	const updatedHits = useMemo(
		() => items.map((hit) => ({ ...hit, id: hit.id, docId: hit.id })),
		[items],
	);

	const searchBoxApi = useSearchBox(props);

	return (
		<AutocompleteInput
			hits={updatedHits}
			setClient={setClient}
			clientId={clientId}
			disabled={disabled}
			clientName={clientName}
			{...searchBoxApi}
			{...{ error, width, onBlur }}
		/>
	);
};
